import type { AdminUser, AuditEntry, Invitation, Organization } from "../api/types";
import { editionText } from "./edition";

/** The state an account is in, as the badges the Users tab shows (most urgent first). */
export function userBadges(u: Pick<AdminUser, "is_active" | "approval_status" | "email_verified_at">): string[] {
  const out: string[] = [];
  if (u.is_active === false) out.push("Disabled");
  if (u.approval_status === "pending") out.push("Awaiting approval");
  if (u.approval_status === "rejected") out.push("Rejected");
  if (u.email_verified_at === null) out.push("Email unconfirmed");
  return out;
}

/** What the Users tab says about why an account is restricted, or "" if it is not. */
export function restrictionNote(u: Pick<AdminUser, "is_active" | "disabled_reason" | "disabled_at">): string {
  if (u.is_active !== false) return "";
  const when = u.disabled_at ? ` on ${u.disabled_at.slice(0, 10)}` : "";
  return `Disabled${when}: ${u.disabled_reason || "no reason recorded"}`;
}

/** An organization's standing, for the Organizations tab. */
export function orgStatus(o: { suspended_at?: string | null; suspension_reason?: string | null }): string {
  return o.suspended_at ? `Suspended ${o.suspended_at.slice(0, 10)}: ${o.suspension_reason || "no reason recorded"}` : "Active";
}

/** What each action on a selected account is allowed to do (the server enforces the same rules). */
export function userActions(selected: AdminUser | null, me: AdminUser | null) {
  if (!selected || !me) return { disable: false, enable: false, approve: false, reject: false, role: false };
  const self = selected.id === me.id;
  const platform = selected.role === "super_admin" && me.role !== "super_admin";
  return {
    disable: !self && !platform && selected.is_active !== false,
    enable: !platform && selected.is_active === false,
    approve: !platform && selected.approval_status === "pending",
    reject: !platform && selected.approval_status === "pending",
    role: !self && !platform,
  };
}

/** Roles an invitation can carry: an admin invites into its own organization. */
export const INVITE_ROLES = ["annotator", "viewer", "admin"] as const;
/** Roles the role picker offers; only a platform administrator may grant super_admin. */
export const roleChoices = (me: Pick<AdminUser, "role"> | null) =>
  me?.role === "super_admin" ? ["viewer", "annotator", "admin", "super_admin"] : ["viewer", "annotator", "admin"];

export const invitationNote = (i: Invitation) =>
  i.status === "pending" ? `expires ${i.expires_at.slice(0, 10)}` : i.status;

/** One line for an audit row: "from annotator, to viewer". */
const plain = (v: unknown): string => (v === null || v === undefined ? "none" : typeof v === "object" ? JSON.stringify(v) : String(v));

/** An audit row's details in words. A change recorded as ``before``/``after`` reads "field: old → new" for what changed. */
export function auditDetail(e: Pick<AuditEntry, "detail">): string {
  const d = (e.detail ?? {}) as Record<string, unknown>;
  const { before, after, ...rest } = d;
  const parts: string[] = [];
  if (before && after && typeof before === "object" && typeof after === "object") {
    const b = before as Record<string, unknown>, a = after as Record<string, unknown>;
    for (const k of Array.from(new Set([...Object.keys(b), ...Object.keys(a)]))) {
      if (plain(b[k]) !== plain(a[k])) parts.push(`${k}: ${plain(b[k])} → ${plain(a[k])}`);
    }
    if (!parts.length) parts.push("no change");
  } else {
    if (before !== undefined) rest.before = before;
    if (after !== undefined) rest.after = after;
  }
  return [...parts, ...Object.entries(rest).map(([k, v]) => `${k} ${plain(v)}`)].join(", ");
}

/** Friendly wording for the audit action codes the server writes. */
export const AUDIT_LABELS: Record<string, string> = {
  "user.signup": "Signed up", "user.create": "Account created", "user.role_change": "Role changed",
  "user.disable": "Account disabled", "user.enable": "Account enabled", "user.approve": "Sign-up approved",
  "user.reject": "Sign-up rejected", "user.password_reset_admin": "Password reset by an administrator",
  "user.password_reset": "Password reset by email", "user.email_verified": "Email confirmed",
  "user.trial_extend": "Trial extended", "user.trial_reset": "Trial reset",
  "invite.create": "Invitation sent", "invite.resend": "Invitation resent", "invite.revoke": "Invitation withdrawn",
  "invite.accept": "Invitation accepted", "org.create": "Organization created",
  "org.suspend": "Organization suspended", "org.unsuspend": "Organization reinstated",
  "org.plan_change": "Trial, seats, storage or edition changed", "org.export": "Data exported",
  "org.purge_started": "Data erasure started", "org.purge": "Data erased",
  "credits.grant": "Credits granted", "credits.rate_change": "Credit rate changed",
  "coupon.mint": "Coupon codes created", "coupon.redeem": "Coupon code redeemed",
  "coupon.revoke": "Coupon code withdrawn", "coupon.revoke_batch": "Coupon batch withdrawn",
};
export const auditLabel = (action: string) => AUDIT_LABELS[action] ?? action;

// ── organization terms ───────────────────────────────────────────────────────

const GB = 2 ** 30;
/** "1.5 GB", "320 MB", "0 B" */
export function bytesLabel(n: number | null | undefined): string {
  const v = n ?? 0;
  if (v >= GB / 10) return `${(v / GB).toFixed(v >= 10 * GB ? 0 : 1)} GB`;
  if (v >= 2 ** 20) return `${Math.round(v / 2 ** 20)} MB`;
  return `${v} B`;
}

export const trialText = (o: Pick<Organization, "trial_state" | "trial_ends_at">): string =>
  o.trial_state === "active" ? `ends ${o.trial_ends_at?.slice(0, 10)}`
    : o.trial_state === "ended" ? `ENDED ${o.trial_ends_at?.slice(0, 10)}` : "none";
export const seatsText = (o: Pick<Organization, "seats_used" | "max_users" | "seats_reserved">): string =>
  `${o.seats_used ?? 0} / ${o.max_users ?? "∞"}` + (o.seats_reserved ? ` (+${o.seats_reserved} invited)` : "");
export const storageText = (o: Pick<Organization, "storage_used_bytes" | "storage_allowance_bytes">): string =>
  `${bytesLabel(o.storage_used_bytes)} / ${o.storage_allowance_bytes ? bytesLabel(o.storage_allowance_bytes) : "∞"}`;

/** A one-line summary of an organization's standing, for its own admins. */
export function planSummary(o: Organization): string {
  const parts = [`Users ${seatsText(o)}`, `Storage ${storageText(o)}`];
  if (o.edition) parts.unshift(`Edition ${editionText(o)}`);
  if (o.trial_state && o.trial_state !== "none") parts.push(`Trial ${trialText(o)}`);
  return parts.join("  ·  ");
}

/** The plan fields the editor changed, as the request body. Blank = remove that limit. */
export function planPatch(
  before: Pick<Organization, "trial_ends_at" | "max_users" | "storage_cap_bytes" | "edition"> & { edition_ends_at?: string | null },
  form: { trialEnds: string; maxUsers: string; storageGb: string; edition?: "standard" | "pro"; editionEnds?: string },
): { patch: { trial_ends_at?: string | null; max_users?: number | null; storage_cap_bytes?: number | null; edition?: "standard" | "pro"; edition_ends_at?: string | null }; error?: string } {
  const patch: { trial_ends_at?: string | null; max_users?: number | null; storage_cap_bytes?: number | null; edition?: "standard" | "pro"; edition_ends_at?: string | null } = {};
  if (form.edition && form.edition !== before.edition) patch.edition = form.edition;
  // "Pro until": only with Pro. Blank = no end.
  if (form.editionEnds !== undefined && (form.edition ?? before.edition) === "pro") {
    const until = form.editionEnds.trim();
    if (until && (!/^\d{4}-\d{2}-\d{2}$/.test(until) || Number.isNaN(Date.parse(until)))) return { patch, error: "Enter the Pro end as a date, YYYY-MM-DD." };
    const was = before.edition_ends_at?.slice(0, 10) ?? "";
    if (until !== was) patch.edition_ends_at = until ? `${until}T23:59:59Z` : null;
  }
  const date = form.trialEnds.trim();
  if (date) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(Date.parse(date))) return { patch, error: "Enter the trial end as a date, YYYY-MM-DD." };
    if (before.trial_ends_at?.slice(0, 10) !== date) patch.trial_ends_at = `${date}T23:59:59Z`;
  } else if (before.trial_ends_at) patch.trial_ends_at = null;

  const users = form.maxUsers.trim();
  if (users) {
    const n = Number(users);
    if (!Number.isInteger(n) || n < 1) return { patch, error: "The user limit must be a whole number, 1 or more (or blank for no limit)." };
    if (before.max_users !== n) patch.max_users = n;
  } else if (before.max_users != null) patch.max_users = null;

  const gb = form.storageGb.trim();
  if (gb) {
    const n = Number(gb);
    if (!(n > 0)) return { patch, error: "The storage cap must be more than 0 GB (or blank for the server default)." };
    const bytes = Math.round(n * GB);
    if (before.storage_cap_bytes !== bytes) patch.storage_cap_bytes = bytes;
  } else if (before.storage_cap_bytes != null) patch.storage_cap_bytes = null;
  return { patch };
}
